/**
 * The per-CRM facts that both the web app and the browser extension need when pushing records.
 * Only plain data lives here — icons, connection checks and transport details stay in the app
 * that owns them, because their shapes differ per surface.
 */
export type PushCRMId = Exclude<CRMSupported, 'salesloft'>;

export interface CRMPushFacts {
  /** Brand name as shown to the user. */
  displayName: string;
  /** Path segment of the push endpoint, e.g. /internal/integration/<segment>/push. */
  apiPathSegment: string;
  /**
   * True when the CRM's API takes 'contact'/'lead' instead of Firmable's raw 'people' category,
   * so the user has to pick which object a people push lands on.
   */
  hasCategorySelect: boolean;
  /** What this CRM calls a lead. Only set when hasCategorySelect is true. */
  leadLabel?: string;
  /** What this CRM calls a company. */
  companyLabel: string;
  /** What this CRM calls a contact, when it isn't simply "Contact". */
  contactLabel?: string;
}

export const CRM_PUSH_FACTS: Record<PushCRMId, CRMPushFacts> = {
  hubspot: {
    displayName: 'HubSpot',
    apiPathSegment: 'hubspot',
    hasCategorySelect: false,
    companyLabel: 'Company',
  },
  salesforce: {
    displayName: 'Salesforce',
    apiPathSegment: 'sf',
    hasCategorySelect: true,
    leadLabel: 'Lead(s)',
    companyLabel: 'Account',
  },
  pipedrive: {
    displayName: 'Pipedrive',
    apiPathSegment: 'pipedrive',
    hasCategorySelect: false,
    companyLabel: 'Organization',
    contactLabel: 'Person',
  },
  msdynamics365sales: {
    displayName: 'MS Dynamics 365 Sales',
    apiPathSegment: 'msdynamics365sales',
    hasCategorySelect: true,
    leadLabel: 'Lead(s)',
    companyLabel: 'Account',
  },
  jobadder: {
    displayName: 'JobAdder',
    apiPathSegment: 'jobadder',
    hasCategorySelect: true,
    leadLabel: 'Candidate(s)',
    companyLabel: 'Company',
  },
};

/**
 * The object a people push lands on. HubSpot and Pipedrive read Firmable's own 'people';
 * the rest need 'contact' unless the user picked 'lead'.
 */
export const resolvePushCategory = (crm: PushCRMId, category: string, selectedCategory?: string) => {
  if (category !== 'people') return category;
  if (!CRM_PUSH_FACTS[crm].hasCategorySelect) return category;
  return selectedCategory ?? 'contact';
};
